// ===========================================
// Social Share Buttons Component
// ===========================================
// Provides share buttons for Facebook, Twitter/X, LinkedIn, and Email

import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Facebook as FacebookIcon,
  X as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useState } from 'react';

// ===========================================
// TYPES
// ===========================================

interface SocialShareButtonsProps {
  title: string;
  url?: string;
  description?: string;
  size?: 'small' | 'medium' | 'large';
}

// ===========================================
// COMPONENT
// ===========================================

export default function SocialShareButtons({
  title,
  url,
  description = '',
  size = 'medium',
}: SocialShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  // Use provided URL or current page URL
  const shareUrl = url || window.location.href;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);

  const handleShare = (platform: string) => {
    let shareLink = '';

    switch (platform) {
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}`;
        break;
      case 'email':
        shareLink = `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0ARead more: ${encodedUrl}`;
        window.location.href = shareLink;
        return;
    }

    // Open social share in a popup window
    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400,noopener,noreferrer');
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const iconSize = size === 'small' ? 'small' : size === 'large' ? 'large' : 'medium';

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Tooltip title="Share on Facebook">
        <IconButton
          onClick={() => handleShare('facebook')}
          size={iconSize}
          sx={{
            color: '#1877F2',
            '&:hover': { bgcolor: 'rgba(24, 119, 242, 0.1)' },
          }}
          aria-label="Share on Facebook"
        >
          <FacebookIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Share on X (Twitter)">
        <IconButton
          onClick={() => handleShare('twitter')}
          size={iconSize}
          sx={{
            color: '#000000',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' },
          }}
          aria-label="Share on X"
        >
          <TwitterIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Share on LinkedIn">
        <IconButton
          onClick={() => handleShare('linkedin')}
          size={iconSize}
          sx={{
            color: '#0A66C2',
            '&:hover': { bgcolor: 'rgba(10, 102, 194, 0.1)' },
          }}
          aria-label="Share on LinkedIn"
        >
          <LinkedInIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Share via Email">
        <IconButton
          onClick={() => handleShare('email')}
          size={iconSize}
          sx={{
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          aria-label="Share via Email"
        >
          <EmailIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
        <IconButton
          onClick={handleCopyLink}
          size={iconSize}
          sx={{
            color: copied ? 'success.main' : 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          aria-label="Copy link"
        >
          <LinkIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
